import { Navigate, Outlet, useLocation } from "react-router-dom";
import { Spinner } from "@/shared/ui";
import { useSessionStore } from "@/shared/stores/sessionStore";
import { paths } from "../routes/paths";
import { useBootIntrospection, useTokenWatcher } from "./useTokenWatcher";

/**
 * Racine authentifiée — remplace la logique de branchement de RootNavigator.
 *
 * ## Le natif choisissait un STACK, le web choisit une REDIRECTION
 * `RootNavigator` rendait `<AuthStack/>` ou `<ClientStack/>` selon l'état : il
 * n'y avait pas d'URL, donc pas de conflit possible entre « où l'utilisateur
 * veut aller » et « où il a le droit d'aller ».
 *
 * Sur web, l'utilisateur peut taper `/wallet` sans session. Il faut donc
 * REDIRIGER — et se souvenir de la destination pour l'y renvoyer après le
 * login, sinon il atterrit sur l'accueil et doit refaire sa navigation.
 * C'est le rôle de `state.from`.
 *
 * ## Pourquoi attendre `_tokensHydrated`
 * Identique au natif : tant que sessionStorage n'a pas été lu, `accessToken`
 * est `undefined` alors que la session est peut-être valide. Rediriger
 * immédiatement provoquerait un aller-retour visible vers /login à
 * chaque rechargement de page. C'est très court (<50 ms, le stockage est
 * synchrone), mais indispensable.
 */
export function RequireAuth() {
  const accessToken = useSessionStore((s) => s.accessToken);
  const tokensHydrated = useSessionStore((s) => s._tokensHydrated);
  const location = useLocation();

  // Ces deux hooks vivent ici parce que c'est le seul point monté pendant toute
  // la durée d'une session authentifiée.
  useTokenWatcher();
  useBootIntrospection();

  if (!tokensHydrated) return <BootScreen />;

  if (!accessToken) {
    return <Navigate to={paths.login()} replace state={{ from: location }} />;
  }

  return <Outlet />;
}

/**
 * Restreint une branche de routes à un rôle.
 *
 * Les rôles viennent du claim Keycloak `realm_access.roles` (cf.
 * extractRoleFromToken). Un CLIENT qui tape `/agent` est renvoyé chez lui, et
 * réciproquement — ce que le natif obtenait « par construction » en montant un
 * stack ou l'autre.
 *
 * ⚠️ Ceci est une garde d'INTERFACE, pas de sécurité. Le bundle JS est
 * entièrement téléchargé par le navigateur : n'importe qui peut lire le code
 * des écrans agent. La seule protection réelle reste l'autorisation côté
 * Gateway sur chaque appel d'API. Ne jamais s'appuyer sur cette garde pour
 * masquer une donnée sensible.
 */
export function RequireRole({ role }: { role: "CLIENT" | "AGENT" }) {
  const current = useSessionStore((s) => s.role);

  if (role === "AGENT" && current !== "AGENT") {
    return <Navigate to={paths.home()} replace />;
  }
  if (role === "CLIENT" && current === "AGENT") {
    return <Navigate to={paths.agentHome()} replace />;
  }

  return <Outlet />;
}

/**
 * Redirige un utilisateur DÉJÀ connecté hors des écrans d'authentification.
 * Sans cela, revenir sur `/login` avec une session valide afficherait un
 * bouton « Se connecter » à quelqu'un qui l'est déjà.
 */
export function RedirectIfAuthenticated() {
  const accessToken = useSessionStore((s) => s.accessToken);
  const role = useSessionStore((s) => s.role);
  const tokensHydrated = useSessionStore((s) => s._tokensHydrated);

  if (!tokensHydrated) return <BootScreen />;

  if (accessToken) {
    return <Navigate to={role === "AGENT" ? paths.agentHome() : paths.home()} replace />;
  }

  return <Outlet />;
}

/**
 * Écran d'attente de l'hydratation. Remplace le SplashScreen natif, qui était
 * une vraie route (`AuthStack initialRouteName="Splash"`).
 *
 * Sur web ce n'est PAS une route : une URL `/splash` n'aurait aucun sens, on ne
 * la partage pas et on ne s'y rend pas. C'est un état de rendu transitoire.
 */
function BootScreen() {
  return (
    <div className="flex h-dvh items-center justify-center bg-white">
      <Spinner size={32} label="Chargement de la session…" />
    </div>
  );
}
